import { Form, Formik } from "formik";
import * as Yup from "yup";
import { FiCheckCircle, FiCircle, FiLock } from "react-icons/fi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import useChangePassword from "api/UserResource/useChangePassword";
import AccountField from "../AccountField";

const ChangePassword = () => {
	const { mutateAsync, isLoading } = useChangePassword();
	const schema = Yup.object().shape({
		current_password: Yup.string().required("Current password is required"),
		password: Yup.string()
			.min(6, "At least 6 characters required")
			.matches(/[a-zA-Z]/, "Include at least one letter")
			.matches(/[0-9]/, "Include at least one number")
			.required("Password is required"),
		password_confirmation: Yup.string()
			.oneOf([Yup.ref("password")], "Passwords don’t match")
			.required("Confirm your new password"),
	});
	return (
		<Formik
			initialValues={{ current_password: "", password: "", password_confirmation: "" }}
			validationSchema={schema}
			onSubmit={async (data, { resetForm, setStatus }) => {
				try {
					await mutateAsync(data);
					resetForm();
					setStatus("saved");
				} catch (_) {
					setStatus("error");
				}
			}}
		>
			{({ values, dirty, isSubmitting, status }) => (
				<Form>
					<div className="account-section-heading">
						<h2>Change your password</h2>
						<p>Enter your current password, then choose a new one.</p>
					</div>
					<div className="account-security-layout">
						<div className="account-security-fields">
							<AccountField
								name="current_password"
								type="password"
								label="Current password"
								autoComplete="current-password"
								required
							/>
							<AccountField
								name="password"
								type="password"
								label="New password"
								autoComplete="new-password"
								required
							/>
							<AccountField
								name="password_confirmation"
								type="password"
								label="Confirm new password"
								autoComplete="new-password"
								required
							/>
						</div>
						<aside className="account-note">
							<FiLock aria-hidden="true" />
							<div>
								<strong>A stronger password, a safer account</strong>Use a unique password you
								haven’t used elsewhere.
								<ul className="account-password-checks" aria-label="Password requirements">
									{[
										{ label: "At least 6 characters", met: values.password.length >= 6 },
										{ label: "At least one letter", met: /[a-zA-Z]/.test(values.password) },
										{ label: "At least one number", met: /[0-9]/.test(values.password) },
									].map(({ label, met }) => (
										<li key={label} className={`account-password-check ${met ? "is-met" : ""}`}>
											{met ? <FiCheckCircle aria-hidden="true" /> : <FiCircle aria-hidden="true" />}
											<span>
												{label}
												<span className="sr-only">{met ? ": met" : ": not met"}</span>
											</span>
										</li>
									))}
								</ul>
							</div>
						</aside>
					</div>
					<div className="account-form-actions">
						<p role="status" aria-live="polite">
							{status === "saved" && !dirty
								? "Password updated successfully."
								: status === "error"
									? "Couldn’t update your password. Please try again."
									: "Use a password manager to keep it safe."}
						</p>
						<button
							type="submit"
							className="account-button"
							disabled={isLoading || isSubmitting || !dirty}
						>
							{isLoading ? (
								<AiOutlineLoading3Quarters className="animate-spin" aria-hidden="true" />
							) : (
								<FiLock aria-hidden="true" />
							)}
							{isLoading ? "Updating…" : "Update password"}
						</button>
					</div>
				</Form>
			)}
		</Formik>
	);
};
export default ChangePassword;
